"use client";

import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { alpha } from "@mui/material/styles";
import { svgIconClasses } from "@mui/material/SvgIcon";
import { toggleButtonClasses } from "@mui/material/ToggleButton";
import { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";
import { gray, green, orange } from "../theme-primitives";

export const inputsCustomizations = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: "border-box",
        transition: "all 100ms ease-in",
        "&:focus-visible": {
          outline: "none",
          outlineOffset: "2px",
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",
        borderRadius: (theme.vars || theme).shape.borderRadius,
        textTransform: "none",
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              height: "2.25rem",
              padding: "8px 12px",
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              height: "2.5rem", // 40px
            },
          },
          {
            props: {
              color: "primary",
              variant: "contained",
            },
            style: {
              color: "white",
              backgroundColor: green[400],
              "&:hover": {
                backgroundColor: green[500],
                boxShadow: "none",
              },
              "&:active": {
                backgroundColor: green[500],
              },
              ...theme.applyStyles("dark", {
                color: "black",
                backgroundColor: green[100],
                "&:hover": {
                  backgroundColor: green[200],
                  boxShadow: "none",
                },
                "&:active": {
                  backgroundColor: green[400],
                },
              }),
            },
          },
          {
            props: {
              color: "primary",
              variant: "subdued",
            },
            style: {
              color: "#047857",
              backgroundColor: "hsla(163, 94%, 70%, 0.4)",
              "&:hover": {
                backgroundColor: "hsla(163, 94%, 60%, 0.4)",
                boxShadow: "none",
              },
              "&:active": {
                backgroundColor: "hsla(163, 94%, 60%, 0.4)",
              },
              ...theme.applyStyles("dark", {
                color: "black",
                backgroundColor: "hsla(163, 94%, 60%, 0.4)",
                "&:hover": {
                  backgroundColor: "hsla(163, 94%, 60%, 0.4)",
                  boxShadow: "none",
                },
                "&:active": {
                  backgroundColor: "hsla(163, 94%, 60%, 0.4)",
                },
              }),
            },
          },
          {
            props: {
              color: "secondary",
              variant: "contained",
            },
            style: {
              color: "white",
              backgroundColor: orange[400],
              border: "none",
              "&:hover": {
                backgroundColor: orange[500],
              },
              "&:active": {
                backgroundColor: orange[500],
              },
            },
          },
          {
            props: {
              variant: "outlined",
              color: "primary",
            },
            style: {
              color: (theme.vars || theme).palette.text.primary,
              border: "1px solid",
              borderColor: green[500],
              background: "none",
              "&:hover": {
                backgroundColor: green[200],
              },
              "&:active": {
                backgroundColor: green[200],
              },
              ...theme.applyStyles("dark", {
                backgroundColor: green[800],
                borderColor: green[700],

                "&:hover": {
                  backgroundColor: green[900],
                  borderColor: green[600],
                },
                "&:active": {
                  backgroundColor: green[900],
                },
              }),
            },
          },
          {
            props: {
              variant: "outlined",
              color: "inverted",
            },
            style: {
              color: "#fff",
              border: "1px solid",
              borderColor: "#fff",
              background: "none",
              "&:hover": {
                backgroundColor: green[200],
              },
              "&:active": {
                backgroundColor: green[200],
              },
              ...theme.applyStyles("dark", {
                backgroundColor: green[800],
                borderColor: green[700],

                "&:hover": {
                  backgroundColor: green[900],
                  borderColor: green[600],
                },
                "&:active": {
                  backgroundColor: green[900],
                },
              }),
            },
          },
          {
            props: {
              color: "secondary",
              variant: "outlined",
            },
            style: {
              color: orange[700],
              border: "1px solid",
              borderColor: orange[500],
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: orange[200],
              },
              "&:active": {
                backgroundColor: orange[200],
              },
              ...theme.applyStyles("dark", {
                color: orange[50],
                border: "1px solid",
                borderColor: orange[900],
                backgroundColor: alpha(orange[900], 0.3),
                "&:hover": {
                  borderColor: orange[700],
                  backgroundColor: alpha(orange[900], 0.6),
                },
                "&:active": {
                  backgroundColor: alpha(orange[900], 0.5),
                },
              }),
            },
          },
          {
            props: {
              variant: "text",
            },
            style: {
              color: green[500],
              backgroundColor: "none",
              padding: "0",
              height: "auto",
              "&:hover": {
                color: green[400],
                background: "none",
              },
              "&:active": {
                color: green[400],
                background: "none",
              },
            },
          },
          {
            props: {
              color: "secondary",
              variant: "text",
            },
            style: {
              color: orange[500],
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: orange[200],
              },
              "&:active": {
                backgroundColor: orange[200],
              },
              ...theme.applyStyles("dark", {
                color: orange[100],
                "&:hover": {
                  backgroundColor: alpha(orange[900], 0.5),
                },
                "&:active": {
                  backgroundColor: alpha(orange[900], 0.3),
                },
              }),
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",
        borderRadius: (theme.vars || theme).shape.borderRadius,
        textTransform: "none",
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: 0,
        color: (theme.vars || theme).palette.text.primary,
        border: "1px solid ",
        borderColor: green[200],
        backgroundColor: alpha(green[50], 0.3),
        "&:hover": {
          backgroundColor: green[100],
          borderColor: green[300],
        },
        "&:active": {
          backgroundColor: green[200],
        },
        ...theme.applyStyles("dark", {
          backgroundColor: green[800],
          borderColor: green[700],
          "&:hover": {
            backgroundColor: green[900],
            borderColor: green[600],
          },
          "&:active": {
            backgroundColor: green[900],
          },
        }),
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              width: "2.25rem",
              height: "2.25rem",
              padding: "0.25rem",
              [`& .${svgIconClasses.root}`]: { fontSize: "1rem" },
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              width: "2.5rem",
              height: "2.5rem",
            },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: "10px",
        boxShadow: `0 4px 16px ${alpha(gray[400], 0.2)}`,
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: green[500],
        },
        ...theme.applyStyles("dark", {
          [`& .${toggleButtonGroupClasses.selected}`]: {
            color: "#fff",
          },
          boxShadow: `0 4px 16px ${alpha(green[700], 0.5)}`,
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: "12px 16px",
        textTransform: "none",
        borderRadius: "10px",
        fontWeight: 500,
        ...theme.applyStyles("dark", {
          color: gray[400],
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
          [`&.${toggleButtonClasses.selected}`]: {
            color: green[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon
          sx={{ color: "hsla(210, 0%, 0%, 0.0)" }}
        />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: "1px solid ",
        borderColor: alpha(gray[300], 0.8),
        boxShadow: "0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset",
        backgroundColor: alpha(gray[100], 0.4),
        transition: "border-color, background-color, 120ms ease-in",
        "&:hover": {
          borderColor: green[300],
        },
        "&.Mui-focusVisible": {
          outline: `3px solid ${alpha(green[500], 0.5)}`,
          outlineOffset: "2px",
          borderColor: green[400],
        },
        "&.Mui-checked": {
          color: "white",
          backgroundColor: green[500],
          borderColor: green[500],
          boxShadow: `none`,
          "&:hover": {
            backgroundColor: green[600],
          },
        },
        ...theme.applyStyles("dark", {
          borderColor: alpha(gray[700], 0.8),
          boxShadow: "0 0 0 1.5px hsl(210, 0%, 0%) inset",
          backgroundColor: alpha(gray[900], 0.8),
          "&:hover": {
            borderColor: green[300],
          },
          "&.Mui-focusVisible": {
            borderColor: green[400],
            outline: `3px solid ${alpha(green[500], 0.5)}`,
            outlineOffset: "2px",
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: "none",
      },
      input: {
        "&::placeholder": {
          opacity: 0.7,
          color: gray[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
      },
      root: ({ theme }) => ({
        padding: "8px 12px",
        color: (theme.vars || theme).palette.text.primary,
        borderRadius: (theme.vars || theme).shape.borderRadius,
        border: `1px solid ${(theme.vars || theme).palette.divider}`,
        backgroundColor: (theme.vars || theme).palette.background.default,
        transition: "border 120ms ease-in",
        "&:hover": {
          borderColor: gray[400],
        },
        [`&.${outlinedInputClasses.focused}`]: {
          outline: `3px solid ${alpha(green[500], 0.5)}`,
          borderColor: green[400],
        },
        ...theme.applyStyles("dark", {
          "&:hover": {
            borderColor: gray[500],
          },
        }),
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              height: "2.25rem",
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              height: "2.5rem",
            },
          },
          {
            props: {
              size: "textarea",
            },
            style: {
              height: "unset",
            },
          },
        ],
      }),
      notchedOutline: {
        border: "none",
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles("dark", {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
      }),
    },
  },
};
